import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { logoutAndRedirect } from '../services/navigation.service';
import appConfig from '../config/app.config';
import { getAccessToken, setAccessToken } from '../features/auth/token-store';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 10000; // Cap exponential backoff

let isRefreshing = false;
type QueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void };
let failedQueue: QueueEntry[] = [];
const retryCounts = new Map<string, number>();

export const http = axios.create({
  baseURL: appConfig.apiBaseUrl,
  withCredentials: true,
});

// Attach access token
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Clear retry count on new requests
  if (config.url) {
    retryCounts.delete(config.url);
  }
  return config;
});

// Helper to calculate exponential backoff delay
const getRetryDelay = (attempt: number): number => {
  const delay = RETRY_DELAY_MS * Math.pow(2, attempt);
  return Math.min(delay, MAX_RETRY_DELAY_MS);
};

// Helper to retry requests with exponential backoff
const retryRequest = (originalRequest: AxiosRequestConfig, attempt: number) => {
  const delay = getRetryDelay(attempt);
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(http(originalRequest));
    }, delay);
  });
};

const onRefreshed = (token: string) => {
  failedQueue.forEach(({ resolve }) => resolve(token));
  failedQueue = [];
};

const onRefreshFailed = (err: unknown) => {
  failedQueue.forEach(({ reject }) => reject(err));
  failedQueue = [];
};

const addSubscriber = (resolve: (token: string) => void, reject: (err: unknown) => void) => {
  failedQueue.push({ resolve, reject });
};

// Interceptor for responses
http.interceptors.response.use(
  (response: AxiosResponse) => {
    // Clear retry count on successful responses
    if (response.config.url) {
      retryCounts.delete(response.config.url);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const url = originalRequest.url || '';

    // Don't retry auth endpoints - they should fail fast
    const isAuthEndpoint = url.includes('/auth/');

    // Retry logic for 5xx and 429 (but not for auth endpoints)
    if (!isAuthEndpoint && [429, 502, 503, 504].includes(status ?? 0)) {
      const currentRetry = retryCounts.get(url) ?? 0;

      if (currentRetry >= MAX_RETRY_ATTEMPTS) {
        retryCounts.delete(url); // Clean up
        return Promise.reject(error);
      }

      retryCounts.set(url, currentRetry + 1);
      return retryRequest(originalRequest, currentRetry);
    }

    // Refresh token logic on 401 - but NOT for auth endpoints
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Enqueue THIS request FIRST so it is picked up by onRefreshed/onRefreshFailed
      // regardless of whether we are the one starting the refresh or just waiting.
      // (The previous bug: async/await caused onRefreshed to fire before addSubscriber
      // was reached, leaving the original request permanently stuck.)
      const retryWithNewToken = new Promise<unknown>((resolve, reject) => {
        addSubscriber(
          (newToken: string) => {
            if (originalRequest.headers) {
              (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
            }
            resolve(http(originalRequest));
          },
          reject
        );
      });

      if (!isRefreshing) {
        isRefreshing = true;
        axios
          .post(`${appConfig.apiBaseUrl}/auth/refresh-token`, {}, { withCredentials: true })
          .then((res) => {
            const newToken = res.data.accessToken;
            setAccessToken(newToken);
            isRefreshing = false;
            onRefreshed(newToken);
          })
          .catch((refreshErr) => {
            isRefreshing = false;
            onRefreshFailed(refreshErr);
            logoutAndRedirect();
          });
      }

      return retryWithNewToken;
    }

    return Promise.reject(error);
  }
);

export default http;
